import Vue from "vue";
import VeeValidate, { Validator } from "vee-validate";
import { ABARoutingNumberIsValid } from "bank-routing-number-validator";
import moment from "moment";
import { validateUsername } from "@/shared/api/userService";
import { formatCurrency } from "@/shared/currency";
import { isNotPwned } from "@/shared/passwordChecker";
import { useUserStore } from "@/shared/store/user";

const config = {
  classes: true,
  classNames : {
    invalid: "is-invalid"
  },
  fieldsBagName: "vvFields"
};

// some of the dollar amounts are coming in as their floating point approximate values
// so this epsilon value is used to increase the allowed range of values for max and min.
// in bug BEND-1511 2925 - 2134.4 - 790 computed to 0.599999999999909 and caused max validator
// to return invalid for input value 0.60
const epsilon = 0.0000001;

// tslint:disable-next-line:variable-name
const min_value_usd = {
  getMessage (field, data) {
    const min = data[0];

    return ("The " + field + " field must be " + formatCurrency(min) + " or more.");
  },
  // copied from vee implementation
  validate (value, ref) {
    const min = ref[0];

    if (Array.isArray(value) || value === null || value === undefined || value === "") {
      return false;
    }

    return Number(value) >= (min - epsilon);
  }
};

// tslint:disable-next-line:variable-name
const max_value_usd = {
  getMessage (field, data) {
    const max = data[0];

    return ("The " + field + " field must be " + formatCurrency(max) + " or less.");
  },
  // copied from vee implementation
  validate (value, ref) {
    const max = ref[0];

    if (Array.isArray(value) || value === null || value === undefined || value === "") {
      return false;
    }

    return Number(value) <= (max + epsilon);
  }
};

const mimimumRecurringAnnualContribution = {
  getMessage (field, data) {
    const min = data[0];

    return ("The annual amount cannot be less than your current year to date contribution of " + formatCurrency(min)) + ".";
  },
  // copied from vee implementation
  validate (value, ref) {
    const min = ref[0];

    if (Array.isArray(value) || value === null || value === undefined || value === "") {
      return false;
    }

    return Number(value) >= (min - epsilon);
  }
};

const forbiddenSubstrings = ["bend", "password"];

const passwordSubstrings = (password: string) => {
    const pwUpper = password.toUpperCase();
    const substrFound = forbiddenSubstrings.find(s => pwUpper.indexOf(s.toUpperCase()) >= 0);

    if (substrFound) {
      return `This password uses the sequence, “${substrFound}”, which makes it easy to guess.`;
    }
    return "";
};

const clientSidePasswordValidator = (password: string) => {
  const otherValidators: ((s: string) => string)[] = [
    passwordNoWhitespace,
    passwordMinimumLength,
    passwordMaximumLength,
    passwordSubstrings,
    passwordComplexity
  ];
  let errorMessage = "";
  otherValidators.find(validatorFunction => {
    errorMessage = validatorFunction(password);
    return !!errorMessage;
  });
  return errorMessage;
};

const clientSidePasswordValidatorHsab = (password: string) => {
  const otherValidators: ((s: string) => string)[] = [
    passwordNoWhitespace,
    passwordMinimumLengthHsab,
    passwordMaximumLength,
    passwordSubstrings,
    passwordComplexityHsab
  ];
  let errorMessage = "";
  otherValidators.find(validatorFunction => {
    errorMessage = validatorFunction(password);
    return !!errorMessage;
  });
  return errorMessage;
};

const passwordValidator = {
  getMessage (field, params, data: string) {
    return data || "Invalid password";
  },
  validate (value): { data?: string, valid: boolean } | Promise<{ data?: string, valid: boolean }> | any {
    const clientSideResult = clientSidePasswordValidator(value);

    if (clientSideResult) {
      return { data: clientSideResult, valid: false};
    }

    return isNotPwned(value)
        .then(v => ({valid: v}))
        .catch(e => ({ data: e.message, valid: false}));
  }
};

const passwordValidatorHsab = {
  getMessage (field, params, data: string) {
    return data || "Invalid password";
  },
  validate (value): { data?: string, valid: boolean } | Promise<{ data?: string, valid: boolean }> | any {
    const clientSideResult = clientSidePasswordValidatorHsab(value);

    if (clientSideResult) {
      return { data: clientSideResult, valid: false };
    }

    return isNotPwned(value)
      .then(v => ({ valid: v }))
      .catch(e => ({ data: e.message, valid: false }));
  }
};

const usernameValidator = {
  getMessage (field, params, data: string) {
    return data || "The provided username is invalid.";
  },
  validate (value, params) {
    return validateUsername({
      username: value,
      bendUserId: params[0]
    })
    .then(() => {
      return { valid: true };
    })
    .catch(error => {
      return { data: error.response.data.errorMessage, valid: false };
    });
  }
};

const errorMessageOrEmpty = (isValid, message: string) => {
  return isValid ? "" : message;
};

const passwordComplexity = (password: string) => {
  const pwTests: RegExp[] = [/[A-Z]/, /[a-z]/, /[0-9]/, /[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`]/];
  const isValid = pwTests.every(re => re.test(password));
  return errorMessageOrEmpty(isValid, "Password must contain at least one upper case character, lower case character, digit and symbol");
};

const passwordComplexityHsab = (password: string) => {
  const pwTests: RegExp[] = [/[A-Z]/, /[a-z]/, /[0-9]/];
  const isValid = pwTests.every(re => re.test(password));
  return errorMessageOrEmpty(isValid, "Password must contain at least one upper case character, lower case character and digit");
};

const passwordNoWhitespace = (password: string) => {
  const isValid = /^[\S]+$/.test(password);
  return errorMessageOrEmpty(isValid, "Password must not contain spaces");
};

const passwordMinimumLength = (password: string) => {
  const userStore = useUserStore();
  const minLength = userStore.passwordMinLength;
  const isValid = password.length >= minLength;
  return errorMessageOrEmpty(isValid, `Password must be at least ${minLength} characters long`);
};

const passwordMinimumLengthHsab = (password: string) => {
  const isValid = /.{12,}/.test(password);
  return errorMessageOrEmpty(isValid, "Password must be at least 12 characters long");
};

const passwordMaximumLength = (password: string) => {
  const isValid = password.length <= 99;
  return errorMessageOrEmpty(isValid, "Password must be no more than 99 characters long");
};

const phone = {
  getMessage (field, params, data) {
    return "Phone number must be in the correct format ###-###-####";
  },
  validate (phone) {
    if (phone.length === 0) {
      return true;
    }
    return /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(phone);
  }
};

const phoneWithDashes = {
  getMessage (field, params, data) {
    return "Phone number must be in the correct format ###-###-####";
  },
  validate (phone) {
    if (phone.length === 0) {
      return true;
    }
    return /^([0-9]{3})-([0-9]{3})-([0-9]{4})$/.test(phone);
  }
};

const phoneWithExtension = {
  getMessage (field, params, data) {
    return "Phone number must be in the correct format ###-###-####x######";
  },
  validate (phone) {
    return /^(?:\d{3}[-. ]){2}\d{4}(?:\s?[-Xx]\d{1,8})?$/.test(phone);
  }
};

const taxid = {
  getMessage (field, params, data) {
    return "Tax id must be EIN format 00-0000000";
  },
  validate (taxid) {
    return /^\d{2}-\d{7}$/.test(taxid);
  }
};

const zipCode = {
  getMessage (field, params, data) {
    return "ZIP code should be in 01234 or 01234-5678 format";
  },
  validate (zipCode) {
    return /^\d{5}(-\d{4})?$/.test(zipCode);
  }
};

const bankrouting = {
  getMessage (field, params, data) {
    return "Your routing number is not in the correct format";
  },
  validate (routingNumber) {
    // the referenced function will autopad with a 0 when the length is 8, but
    // our older validation library didn't do this. So I'm requiring 9 to be consistent
    return routingNumber.length && routingNumber.length === 9 && ABARoutingNumberIsValid(routingNumber);
  }
};

const bankaccount = {
  getMessage (field, params, data) {
    return (data) || "Your bank account is not in the correct format";
  },
  validate (bankAccount): { data: string, valid: boolean } | any {
    if (typeof bankAccount !== "string") {
      return { data: "Invalid bank account number", valid: false };
    }

    const trimmedAccountNumber = (bankAccount || "").trim();
    if (!/^\d+$/.test(trimmedAccountNumber)) {
      return { data: "Must be numeric", valid: false };
    }
    if (trimmedAccountNumber.length < 4) {
      return { data: "Must be longer than 3 digits", valid: false };
    }
    if (trimmedAccountNumber.length > 17) {
      return { data: "Must be fewer than 18 digits", valid: false };
    }

    return { data: null, valid: true };
  }
};

const productCode = {
  getMessage (field, params, data) {
    return data || "Product code is not in the correct format.";
  },
  validate (productCode): { data: string, valid: boolean } | any {
    if (typeof productCode !== "string") {
      return { data: "Invalid product code.", valid: false };
    }

    const trimmedProductCode = (productCode || "").trim();

    if (!/^\d+$/.test(trimmedProductCode)) {
      return { data: "Product code must be numeric.", valid: false };
    }

    if (trimmedProductCode.length !== 12 && trimmedProductCode.length !== 14) {
      return { data: "Product code must be exactly 12 or 14 digits long.", valid: false };
    }

    return { data: null, valid: true };
  }
};

const versionNumber = {
  getMessage (field, params, data) {
    return data || "Version is not in the correct format.";
  },
  validate (versionNumber): { data: string, valid: boolean } | any {
    if (typeof versionNumber !== "string") {
      return { data: "Invalid version.", valid: false };
    }

    const trimmedVersionNumber = (versionNumber || "").trim();

    if (!/^\d+\.\d+\.\d+$/.test(trimmedVersionNumber)) {
      return { data: "Version is not in the correct format.", valid: false };
    }

    return { data: null, valid: true };
  }
};

const validateIsFutureDate = {
  getMessage (field, params, data) {
    return "Date cannot be in the past";
  },
  validate: (payDate): any | string => {
    return moment(payDate).isAfter(moment());
  }
};

const validateIsNotFutureDate = {
  getMessage (field, params, data) {
    return "Date cannot be after " + moment().format("MM/DD/YYYY");
  },
  validate (value) {
    return !validateIsFutureDate.validate(value);
  }
};

const isNotPoBox = {
  getMessage (field, params, data) {
    return "Address must be a physical address not a PO Box";
  },
  validate (addressLine) {
    // tslint:disable-next-line:max-line-length
    const poBox = /^ *(((box|bin)[-. /\\]?\d+)|(.*p[ .]? ?(o|0)[-. /\\]? *-?((box|bin)|b|(num)?\d+))|(p(ost)? *(o(ff(ice)?)?)? *((box|bin)|b)? *\d+)|(p *-?\/?(o)? *-?box)|post office box|((box|bin)|b) *(number|num)? *\d+|(num|number) *\d+)/i;
    return !poBox.test(addressLine);
  }
};

const resemblesAddressLine = {
  getMessage (field, params, data) {
    if (data && data.message) {
      return data.message.replace("{field}", field);
    }

    return `${field} is invalid`;
  },
  validate (addressLine): { valid: boolean, data?: any } {
    const allSpaces = /^[ ]+$/;
    if (allSpaces.test(addressLine)) {
      return { valid: true }; // required field validator will catch this
    }

    const allDigits = /^[^A-Z]+$/i; // input lacks any alphabetical characters
    if (allDigits.test(addressLine)) {
      return { valid: false, data: { message: "{field} is incomplete" } };
    }

    const valid = /^[a-zA-Z0-9- ]+$/i.test(addressLine);
    if (!valid) {
      return { valid: false, data: { message: "{field} can only use letters, numbers, hyphens and spaces" } };
    }

    return { valid: true };
  }
};

const social = {
  getMessage (field, params, data) {
    return "Please enter a valid SSN 000-00-0000";
  },
  validate (ssn) {
    return  /^\d{3}-\d{2}-\d{4}$/.test(ssn);
  }
};

const over18 = {
  getMessage (field, params, data) {
    return "You must be at least 18 years old to open an HSA.";
  },
  validate: (dob: string) => {
    const dobMoment = moment(dob);
    const now = moment();
    return now.diff(dobMoment, "years") >= 18;
  }
};

const over18AuthorizedSigner = {
  getMessage (field, params, data) {
    return "Authorized signer must be at least 18 years old.";
  },
  validate: over18.validate
};

const over120 = {
  getMessage (field, params, data) {
    return "The birth date entry is not valid.";
  },
  validate: (dob: string) => {
    const minAllowed = moment().subtract(120, "years").startOf("day");
    const match = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

    if (match) {
      const year = parseInt(match[3], 10);
      if (year < minAllowed.year()) {
        return false;
      }
    }

    const dobMoment = moment(dob).startOf("day");
    return !dobMoment.isBefore(minAllowed);
  }
};

const agreeTo = {
  getMessage (field, params, data) {
    return `You must agree to ${field} to proceed.`;
  },
  validate: (b: boolean) => {
    return b;
  }
};

const hexColor = {
  getMessage (field, params, data) {
    return "It is not valid hex color code.";
  },
  validate (hexColor) {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexColor);
  }
};

const urlWithHttp = {
  getMessage (field, params, data) {
    return "URL must start with http:// or https://";
  },
  validate (url: string) {
    return /^https?:\/\/.+/.test(url);
  }
};

const isNotHtml = {
  getMessage (field, params, data) {
    return "Field cannot contain HTML, just plain text.";
  },
  validate (str: string) {
    return (str.includes("<") && str.includes(">")) === false;
  }
};

const doesNotHaveNewLine = {
  getMessage (field, params, data) {
    return "Text must only be one line.";
  },
  validate (str: string) {
    return (str.includes("\n") || str.includes("\r")) === false;
  }
};

const validateDateIsBefore = {
  getMessage (fromField, toField) {
    return fromField + " must be before " + toField;
  },
  validate (value, args) {
    if (args[0] === "null") {
      return true;
    }
    return moment(value).isSameOrBefore(args[0]);
  }
};

const validateDateIsAfter = {
  getMessage (toField, fromField) {
    return toField + " must be after " + fromField;
  },
  validate (toDate, args) {
    if (args[0] === "null") {
      return true;
    }
    return moment(toDate).isSameOrAfter(args[0]);
  }
};

const dependentNameValidator = {
  getMessage (field, params, data) {
    return "Name contains unsupported characters.";
  },
  validate (name) {
    return /^[A-Z0-9&',/#.-]+$/gi.test(name);
  }
};

const alphaNumericSpace = {
  getMessage (field, params, data) {
    return `${field} contains unsupported characters.`;
  },
  validate (name) {
    return /^[a-zA-Z0-9 ]*$/gi.test(name);
  }
};

const nameValidatorWex = {
  getMessage (field) {
    return `${field} cannot contain special characters except a single quote (') or a dash (-).`;
  },
  validate (name) {
    return /^[A-Z0-9-' ]+$/gi.test(name);
  }
};

const atLeastOneUppercase = {
  getMessage () {
    return "invalid";
  },
  validate (value: string) {
    return /[A-Z]/.test(value);
  }
};

const atLeastOneLowercase = {
  getMessage () {
    return "invalid";
  },
  validate (value: string) {
    return /[a-z]/.test(value);
  }
};

const atLeastOneNumber = {
  getMessage () {
    return "invalid";
  },
  validate (value: string) {
    return /[0-9]/.test(value);
  }
};

const noWhitespace = {
  getMessage () {
    return "invalid";
  },
  validate (value: string) {
    if (!value) {
      return true;
    }

    return /^\S+$/.test(value);
  }
};

const doesNotContain = {
  getMessage () {
    return "invalid";
  },
  validate (value: string, params: string[]) {
    if (!value) {
      return true;
    }

    const substring = params[0];

    if (!substring) {
      return true;
    }

    return !value.toLowerCase().includes(substring.toLowerCase());
  }
};

const attachValidation = () => {
  Validator.extend("phone", phone);
  Validator.extend("taxid", taxid);
  Validator.extend("zipCode", zipCode);
  Validator.extend("bankrouting", bankrouting);
  Validator.extend("bankaccount", bankaccount);
  Validator.extend("isNotPoBox", isNotPoBox);
  Validator.extend("validateIsFutureDate", validateIsFutureDate);
  Validator.extend("validateIsNotFutureDate", validateIsNotFutureDate);
  Validator.extend("phoneWithExtension", phoneWithExtension);
  Validator.extend("min_value_usd", min_value_usd);
  Validator.extend("max_value_usd", max_value_usd);
  Validator.extend("mimimumRecurringAnnualContribution", mimimumRecurringAnnualContribution);
  Validator.extend("ssn", social);
  Validator.extend("over18", over18);
  Validator.extend("over18AuthorizedSigner", over18AuthorizedSigner);
  Validator.extend("over120", over120);
  Validator.extend("agreeTo", agreeTo);
  Validator.extend("passwordValidator", passwordValidator);
  Validator.extend("passwordValidatorHsab", passwordValidatorHsab);
  Validator.extend("resemblesAddressLine", resemblesAddressLine);
  Validator.extend("hexColor", hexColor);
  Validator.extend("usernameValidator", usernameValidator);
  Validator.extend("urlWithHttp", urlWithHttp);
  Validator.extend("isNotHtml", isNotHtml);
  Validator.extend("doesNotHaveNewLine", doesNotHaveNewLine);
  Validator.extend("validateDateIsBefore", validateDateIsBefore);
  Validator.extend("validateDateIsAfter", validateDateIsAfter);
  Validator.extend("productCode", productCode);
  Validator.extend("versionNumber", versionNumber);
  Validator.extend("dependentNameValidator", dependentNameValidator);
  Validator.extend("alphaNumericSpace", alphaNumericSpace);
  Validator.extend("phoneWithDashes", phoneWithDashes);
  Validator.extend("nameValidatorWex", nameValidatorWex);
  Validator.extend("atLeastOneUppercase", atLeastOneUppercase);
  Validator.extend("atLeastOneLowercase", atLeastOneLowercase);
  Validator.extend("atLeastOneNumber", atLeastOneNumber);
  Validator.extend("noWhitespace", noWhitespace);
  Validator.extend("doesNotContain", doesNotContain);
  Vue.use(VeeValidate, config);
};

export {
  attachValidation
};
